import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';

interface WizardData {
  step1?: { projectType?: string };
  step2?: { description?: string };
  step3?: { features?: string[]; customFeatures?: string };
  step4?: { designStyle?: string; colorPreference?: string };
  step5?: { expectedUsers?: string; userRoles?: string };
  step6?: { integrations?: string[] };
  step7?: { timeline?: string; budgetRange?: string };
}

interface Step8Props {
  onSubmit: () => void;
  onBack: () => void;
  wizardData: WizardData;
  quoteEstimate?: { min: number; max: number };
  isSubmitting?: boolean;
}

export const Step8Review = ({ onSubmit, onBack, wizardData, quoteEstimate, isSubmitting }: Step8Props) => {
  const formatCurrency = (amount: number) => {
    return `R${amount.toLocaleString('en-ZA')}`;
  };

  const projectTypeLabels: Record<string, string> = {
    web_app: 'Web Application',
    mobile_app: 'Mobile Application',
    api_backend: 'API/Backend',
    ecommerce: 'E-commerce',
    dashboard: 'Dashboard/Portal',
    ai_ml: 'AI/ML Solution',
    other: 'Other',
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Review & Submit</h2>
        <p className="text-gray-600 mt-2">Review your project details before submitting</p>
      </div>

      <Card className="border-2 border-gray-200">
        <CardContent className="pt-6 space-y-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-500 uppercase">Project Type</h3>
            <p className="text-lg text-gray-900 mt-1">
              {wizardData.step1?.projectType ? projectTypeLabels[wizardData.step1.projectType] : 'Not specified'}
            </p>
          </div>

          <div className="border-t pt-4">
            <h3 className="text-sm font-semibold text-gray-500 uppercase">Description</h3>
            <p className="text-gray-900 mt-1 whitespace-pre-wrap">
              {wizardData.step2?.description || 'Not provided'}
            </p>
          </div>

          <div className="border-t pt-4">
            <h3 className="text-sm font-semibold text-gray-500 uppercase">Features</h3>
            <ul className="mt-2 space-y-1">
              {wizardData.step3?.features?.map((feature: string) => (
                <li key={feature} className="text-gray-900">
                  • {feature}
                </li>
              )) || <li className="text-gray-500">None selected</li>}
            </ul>
            {wizardData.step3?.customFeatures && (
              <p className="text-gray-700 mt-2 italic">{wizardData.step3.customFeatures}</p>
            )}
          </div>

          <div className="border-t pt-4">
            <h3 className="text-sm font-semibold text-gray-500 uppercase">Design Preferences</h3>
            <p className="text-gray-900 mt-1">
              Style: {wizardData.step4?.designStyle || 'Not specified'}
            </p>
            {wizardData.step4?.colorPreference && (
              <p className="text-gray-700">Colors: {wizardData.step4.colorPreference}</p>
            )}
          </div>

          <div className="border-t pt-4">
            <h3 className="text-sm font-semibold text-gray-500 uppercase">Users & Scale</h3>
            <p className="text-gray-900 mt-1">
              Expected Users: {wizardData.step5?.expectedUsers || 'Not specified'}
            </p>
            {wizardData.step5?.userRoles && (
              <p className="text-gray-700">Roles: {wizardData.step5.userRoles}</p>
            )}
          </div>

          <div className="border-t pt-4">
            <h3 className="text-sm font-semibold text-gray-500 uppercase">Integrations</h3>
            <ul className="mt-2 space-y-1">
              {wizardData.step6?.integrations && wizardData.step6.integrations.length > 0 ? (
                wizardData.step6.integrations.map((integration: string) => (
                  <li key={integration} className="text-gray-900">
                    • {integration}
                  </li>
                ))
              ) : (
                <li className="text-gray-500">None selected</li>
              )}
            </ul>
          </div>

          <div className="border-t pt-4">
            <h3 className="text-sm font-semibold text-gray-500 uppercase">Timeline & Budget</h3>
            <p className="text-gray-900 mt-1">
              Timeline: {wizardData.step7?.timeline || 'Not specified'}
            </p>
            <p className="text-gray-900">
              Budget Range: {wizardData.step7?.budgetRange || 'Not specified'}
            </p>
          </div>
        </CardContent>
      </Card>

      {quoteEstimate && (
        <Card className="border-2 border-blue-600 bg-blue-50">
          <CardContent className="pt-6">
            <h3 className="text-lg font-bold text-blue-900">Estimated Quote</h3>
            <p className="text-3xl font-bold text-blue-600 mt-2">
              {formatCurrency(quoteEstimate.min)} - {formatCurrency(quoteEstimate.max)}
            </p>
            <p className="text-sm text-blue-800 mt-2">
              This is a preliminary estimate. A detailed quote will be provided after review.
            </p>
          </CardContent>
        </Card>
      )}

      <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4">
        <p className="text-sm text-cyan-900">
          <strong>What happens next?</strong> Our team will review your requirements and provide a
          detailed quote within 24 hours. We'll contact you via email to discuss your project further.
        </p>
      </div>

      <div className="flex justify-between">
        <Button onClick={onBack} variant="outline" className="px-8" disabled={isSubmitting}>
          Back
        </Button>
        <Button
          onClick={onSubmit}
          disabled={isSubmitting}
          className="bg-blue-600 hover:bg-blue-700 text-white px-8"
        >
          {isSubmitting ? 'Submitting...' : 'Submit Project Request'}
        </Button>
      </div>
    </div>
  );
};
